<template>
  <div
    v-click-outside="onClickOutside"
    class="d-flex flex-column align-center"
    style="gap: 5px"
  >
    <z-selected-values-input
      :max-displayed-values="maxDisplayedValues"
      :input-value="inputValue"
      :input-placeholder="inputPlaceholder"
      :value="formattedValues"
      :show-all="showAll"
      @update:show-all="onShowAll"
      @click:additional-items="$emit('click:additional-items', $event)"
      @focus="shouldShowCard = true"
      @input="$emit('update:inputValue', $event)"
      @click:remove="onRemoveChip"
    >
      <template #value="{value}">
        <slot name="value" :value="value"></slot>
      </template>
    </z-selected-values-input>

    <v-expand-transition>
      <template v-if="shouldShowCard">
        <div style="width: 100%; max-width: 100%">
          <history-card
            v-if="!inputValue && historyAndRecommendations.length > 0"
            key="history-card"
            :max-displayed-items="maxDisplayedItems"
            :loading="loadingHistory"
            :max-height="maxHeight"
            :history="historyItems"
            :recommendations="recommendationItems"
            @include="onInclude"
            @exclude="onExclude"
          ></history-card>

          <search-card
            v-else
            key="search-card"
            :max-displayed-items="maxDisplayedItems"
            :loading="loadingSearch"
            :max-height="maxHeight"
            :items="searchItems"
            :input-value="inputValue"
            @select-all="onSelectAll"
            @include="onInclude"
            @exclude="onExclude"
          ></search-card>
        </div>
      </template>
    </v-expand-transition>
  </div>
</template>

<script>
import HistoryCard from './components/HistoryCard.vue'
import SearchCard from './components/SearchCard.vue'

export default {
  name: 'ZSearchAutocomplete',
  components: {HistoryCard, SearchCard},
  props: {
    selectedValues: {
      type: Array,
      default: () => [],
    },
    history: {
      type: Array,
      default: () => [],
    },
    recommendations: {
      type: Array,
      default: () => [],
    },

    inputValue: {
      type: String,
      default: '',
    },

    inputPlaceholder: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => [],
    },

    maxHeight: {
      type: String,
      default: '200px',
    },

    loadingHistory: {
      type: Boolean,
      default: false,
    },

    loadingSearch: {
      type: Boolean,
      default: false,
    },

    maxDisplayedValues: {
      type: Number,
      default: 50,
    },

    maxDisplayedItems: {
      type: Number,
      default: 50,
    },
  },

  data() {
    return {
      shouldShowCard: false,
      showAll: false,
    }
  },

  computed: {
    formattedValues() {
      const mappedValues = this.selectedValues.map((item) => {
        return {
          ...item,
          color: item.type === 'include' ? 'var(--primary)' : 'var(--red-400)',
        }
      })

      return mappedValues
    },

    historyAndRecommendations() {
      return this.history.concat(this.recommendations)
    },

    historyItems() {
      return this.history.filter(
        (item) => !this.selectedValues.map((i) => i.value).includes(item.value)
      )
    },

    recommendationItems() {
      return this.recommendations.filter(
        (item) => !this.selectedValues.map((i) => i.value).includes(item.value)
      )
    },

    searchItems() {
      return this.items.filter(
        (item) => !this.selectedValues.map((i) => i.value).includes(item.value)
      )
    },
  },

  watch: {
    shouldShowCard(value) {
      this.$emit('update:show-card', value)
    },
  },

  methods: {
    onShowAll(value) {
      if (!value) {
        this.$emit('click:show-less')
      } else {
        this.$emit('click:show-all')
      }

      this.showAll = value
    },

    onClickOutside() {
      this.shouldShowCard = false
      this.onShowAll(false)
      this.$emit('update:inputValue', '')
    },

    /**
     * @param {Array<{text: string, value: any, type: 'include' | 'exclude'}>} items
     */
    onSelectAll(items) {
      this.$emit('update:selectedValues', [...this.selectedValues, ...items])
      this.shouldShowCard = false
    },

    /**
     * @param {{text: string, value: any, type: 'include' | 'exclude'}} item
     * @param {'history' | 'recommendations' | 'items'} list
     */
    onInclude(item, list) {
      const newList = this[list].map((i) => {
        if (i.value === item.value) {
          return {...i, type: 'include'}
        }

        return i
      })

      this.$emit(`update:${list}`, newList)
      this.$emit('update:selectedValues', [
        ...this.selectedValues,
        {...item, type: 'include'},
      ])
      this.$emit('update:inputValue', '')
    },

    /**
     * @param {{text: string, value: any, type: 'include' | 'exclude'}} item
     * @param {'history' | 'recommendations' | 'items'} list
     */
    onExclude(item, list) {
      const newList = this[list].map((i) => {
        if (i.value === item.value) {
          return {...i, type: 'exclude'}
        }

        return i
      })

      this.$emit(`update:${list}`, newList)
      this.$emit('update:selectedValues', [
        ...this.selectedValues,
        {...item, type: 'exclude'},
      ])
      this.$emit('update:inputValue', '')
    },

    /**
     * @param {{text: string, value: any, type: 'include' | 'exclude'}} item
     */
    onRemoveChip(item) {
      const newValues = this.selectedValues.filter(
        (i) => i.value !== item.value
      )

      this.$emit('update:selectedValues', newValues)
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.v-subheader) {
  width: calc(100% - 15px) !important;
  height: 30px !important;
  min-height: 30px !important;
  font-weight: 400;
  font-size: 11px;
  color: #666666;
  position: sticky;
  background: white;
  top: 0;
}
</style>
